import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { GradebookFilter, AssignmentType } from '../types';

interface GradebookFiltersProps {
  filters: GradebookFilter;
  onFiltersChange: (filters: GradebookFilter) => void;
  onReset: () => void;
}

const GradebookFilters = ({
  filters,
  onFiltersChange,
  onReset,
}: GradebookFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const assignmentTypeOptions = [
    { value: 'homework', label: 'Temă' },
    { value: 'quiz', label: 'Test' },
    { value: 'exam', label: 'Examen' },
    { value: 'project', label: 'Proiect' },
    { value: 'participation', label: 'Participare' },
  ];

  const studentGroupOptions = [
    { value: 'all', label: 'Toți elevii' },
    { value: 'group-a', label: 'Grupa A' },
    { value: 'group-b', label: 'Grupa B' },
    { value: 'struggling', label: 'Necesită sprijin' },
    { value: 'advanced', label: 'Avansați' },
  ];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      searchQuery: e.target.value,
    });
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        ...filters.dateRange,
        [field]: value,
      },
    });
  };

  const handleAssignmentTypeToggle = (type: AssignmentType) => {
    const newTypes = filters.assignmentTypes.includes(type)
      ? filters.assignmentTypes.filter((t) => t !== type)
      : [...filters.assignmentTypes, type];

    onFiltersChange({
      ...filters,
      assignmentTypes: newTypes,
    });
  };

  const handleStudentGroupChange = (value: string | number) => {
    onFiltersChange({
      ...filters,
      studentGroups: [value.toString()],
    });
  };

  return (
    <div className="bg-card border-b border-border">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Caută elevi după nume sau ID..."
              value={filters.searchQuery}
              onChange={handleSearchChange}
              className="w-full"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              iconPosition="right"
            >
              Filtre
            </Button>
            <Button variant="ghost" onClick={onReset} iconName="X" iconPosition="left">
              Resetează
            </Button>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border animate-scale-in">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Interval de date
                </label>
                <div className="space-y-2">
                  <Input
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => handleDateRangeChange('start', e.target.value)}
                    placeholder="Data început"
                  />
                  <Input
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => handleDateRangeChange('end', e.target.value)}
                    placeholder="Data sfârșit"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tipuri evaluări
                </label>
                <div className="space-y-2">
                  {assignmentTypeOptions.map((option) => (
                    <Checkbox
                      key={option.value}
                      label={option.label}
                      checked={filters.assignmentTypes.includes(
                        option.value as AssignmentType
                      )}
                      onChange={() =>
                        handleAssignmentTypeToggle(option.value as AssignmentType)
                      }
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Grupe de elevi
                </label>
                <Select
                  options={studentGroupOptions}
                  value={filters.studentGroups[0] || 'all'}
                  onChange={handleStudentGroupChange}
                  placeholder="Selectează grupa"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GradebookFilters;
